import {
  Button,
  Drawer,
  Image,
  message,
  Select,
  Skeleton
} from "antd";
import React, { FC, useEffect, useState } from "react";


//!API
import * as TEACHER_SERVICES from "api/services/teacher/teacher.services";

//!IMAGES
import LogoClasses from "assets/images/teacher/teacher_classes.svg";
import ArrowBack from "../../assets/images/app/arrow_back.svg";

//!STYLE
import { TransferItem } from "antd/es/transfer";
import { StatusCode } from "common/enums";
import AppResult from "components/appResults";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import TableTransfer from "screens/teacher/organisme/components/tableTransfert";
import { useGetOrganismRoomsQuery } from "store/services/teacher/classes.services";
import s from "./style.module.css";

// Define the shape of a student item
interface Student extends TransferItem {
  lastname: string;
  firstname: string,
  level: string;
  credits: number;
}

type Props = {
  showInviteStudents: boolean;
  setShowInviteStudents: React.Dispatch<React.SetStateAction<boolean>>;
};

const { Option } = Select;

const ParentOrganismInviteStudents: FC<Props> = ({
  showInviteStudents,
  setShowInviteStudents,
}) => {

  //!STATES
  const { user } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState<boolean>(false);
  const [isInviting, setIsInviting] = useState<boolean>(false);
  const [addCourseError, setAddCourseError] = useState<string>("");

  const { data: rooms, isLoading, isError, refetch } = useGetOrganismRoomsQuery(user?._id);

  const [roomId, setRoomId] = useState<string>();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [organismeStudents, setOrganismeStudents] = useState<any[]>([]);
  const [studentsList, setStudentsList] = useState<any[]>([]);
  const [targetKeys, setTargetKeys] = useState<string[]>([]);
  const [isDataLoading, setIsDataLoading] = useState<boolean>(false);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);
  const [isTablet, setIsTablet] = useState(window.innerWidth > 767 && window.innerWidth <= 1024);

  const handleResize = () => {
    setIsMobile(window.innerWidth <= 767);
    setIsTablet(window.innerWidth > 767 && window.innerWidth <= 1024);
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchStudentsList = async (organismeId: string, roomID: string) => {
    try {
      setIsDataLoading(true);
      const response = await TEACHER_SERVICES.getOrganismeClassroomStudents(organismeId, roomID);
      if (response?.statusCode === StatusCode.OK) {
        setOrganismeStudents(response?.data);
      }
    } catch (error) {
    }
    finally {
      setIsDataLoading(false);
    }
  };

  const handleInviteStudents = async () => {
    if (!roomId) {
      message.warning("Veuillez choisir une classe !");
      return;
    }

    try {
      setIsInviting(true);

      const results = await Promise.all(
        studentsList.map(async (student: any) => {
          try {
            const response = await TEACHER_SERVICES.addStudentToClassroom(
              roomId,
              student?.key
            );

            if (response?.statusCode !== StatusCode.CREATED) {
              throw new Error("Failed");
            }

            return { success: true, studentId: student?.key };
          } catch (error) {
            return { success: false, studentId: student?.key };
          }
        })
      );

      const successCount = results.filter(r => r.success).length;
      const errors = results.filter(r => !r.success);

      if (successCount > 0) {
        message.success(successCount > 1 ? "Elèves ajoutés avec succès" : "Elève ajouté avec succès");
        setShowInviteStudents(false);
      }

      errors.forEach(() => {
        message.error("Erreur lors de l'ajout d'un élève");
      });

    } catch (error) {
      console.log(error);
    } finally {
      setIsInviting(false);
    }
  };

  // Sample data for students
  const students: Student[] = organismeStudents.map((student, index) => ({
    key: student?.id,
    firstname: student?.firstname,
    lastname: student?.lastname,
    level: student?.level,
    credits: student?.credits,
  }));

  const onChange = (nextTargetKeys: string[], direction: string, moveKeys: string[]) => {
    setTargetKeys(nextTargetKeys);

    // Find students that were moved to the right column
    if (direction === 'right') {
      const movedStudents = students.filter(student =>
        student.key && moveKeys.includes(student.key)  // Ensure student.key is defined
      );
      setStudentsList(prevStudents => [...prevStudents, ...movedStudents]);
    }

    // Handle students moved to the left column
    if (direction === 'left') {
      const removedStudents = students.filter(student =>
        student.key && moveKeys.includes(student.key)  // Ensure student.key is defined
      );
      setStudentsList(prevStudents =>
        prevStudents.filter(student => !removedStudents.some(s => s.key === student.key))
      );
    }
  };

  // Define columns for the transfer table - responsive widths
  const getColumnWidth = () => {
    if (isMobile) return 120;
    if (isTablet) return 180;
    return 250;
  };

  const leftColumns = [
    { dataIndex: 'lastname', title: 'Nom', width: getColumnWidth() },
    { dataIndex: 'firstname', title: 'Prénoms', width: getColumnWidth() },
    {
      dataIndex: 'level',
      title: 'Niveau',
      width: getColumnWidth(),
      render: (levelId: string) => {
        const backgroundColor = '#ebfefb';
        return (
          <div style={{ backgroundColor, padding: '4px 8px', borderRadius: '8px', fontSize: isMobile ? '0.8em' : '1em' }}>
            {levelId}
          </div>
        );
      }
    },
  ];

  const rightColumns = [
    { dataIndex: 'lastname', title: 'Nom', width: getColumnWidth() },
    { dataIndex: 'firstname', title: 'Prénoms', width: getColumnWidth() },
    {
      dataIndex: 'level',
      title: 'Niveau',
      width: getColumnWidth(),
      render: (levelId: string) => {
        const backgroundColor = '#ebfefb';
        return (
          <div style={{ backgroundColor, padding: '4px 8px', borderRadius: '8px', fontSize: isMobile ? '0.8em' : '1em' }}>
            {levelId}
          </div>
        );
      }
    },
  ];

  useEffect(() => {
    if (roomId) {
      fetchStudentsList(user?._id, roomId);
    }
  }, [user, roomId]);

  return (
    <Drawer
      className="drawer"
      title={false}
      placement="right"
      width={isMobile ? "100%" : isTablet ? "80%" : "65%"}
      onClose={() => setShowInviteStudents!(!showInviteStudents)}
      visible={showInviteStudents}
      closable={true}
      headerStyle={{
        border: "none",
        paddingTop: isMobile ? "30px" : "50px",
        paddingLeft: isMobile ? "15px" : isTablet ? "30px" : "40px",
        backgroundColor: "transparent"
      }}
      closeIcon={
        <img
          src={ArrowBack}
          height={isMobile ? 10 : 18}
          alt="Back"
        />
      }
      drawerStyle={{
        backgroundColor: "#FFF",
        borderTopLeftRadius: isMobile ? "0px" : "50px",
        borderBottomLeftRadius: isMobile ? "0px" : "50px",
        boxShadow: "-3px 3px 34px #39393929"
      }}
      bodyStyle={{
        padding: isMobile ? "15px" : isTablet ? "20px" : "24px"
      }}
    >
      <div className={s.drawer}>
        <div className={s.drawer__content}>
          <div className={s.drawer___header}>
            <Image preview={false} src={LogoClasses} width={isMobile ? 60 : isTablet ? 70 : 80} />
            <h3>Inviter des élèves</h3>
          </div>

          <div className={s.selectClassBox}>
            <Select
              value={roomId}
              onChange={(value) => setRoomId(value)}
              className="select-filter"
              placeholder={<span style={{ fontSize: "12px" }}>Choisir une classe</span>}
            >
              {rooms?.data?.map((room: any, index: number) => (
                <Option key={index} value={room?._id}>{room?.title}</Option>
              ))}
            </Select>
          </div>

          {isDataLoading && isLoading ? <Skeleton /> : !isDataLoading && !isLoading && roomId && <div style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "auto",
            paddingBottom: isMobile ? "20px" : "5%",
            borderRadius: "12px",
            marginBottom: isMobile ? "40px" : "100px",
            width: "100%",
            overflowX: isMobile ? "hidden" : "visible"
          }}>
            {organismeStudents.length !== 0 && <div style={{
              fontSize: isMobile ? "14px" : "16px",
              color: "#000",
              marginBottom: isMobile ? "15px" : "20px",
              textAlign: "center",
              padding: isMobile ? "0 10px" : "0"
            }}>
              Sélectionner les étudiants
            </div>}
            {organismeStudents.length === 0 ?
              <AppResult status={"info"} title={"Aucun élève trouvé dans ta recherche. Choisis un organisme."} /> :
              <div style={{
                width: "100%",
                overflowX: isMobile ? "auto" : "visible",
                padding: isMobile ? "0" : "0 10px"
              }}>
                <TableTransfer
                  dataSource={students}
                  targetKeys={targetKeys}
                  onChange={onChange}
                  filterOption={(inputValue, item) =>
                    item.lastname.toLowerCase().includes(inputValue.toLowerCase()) ||
                    item.firstname.toLowerCase().includes(inputValue.toLowerCase()) ||
                    item.level.toLowerCase().includes(inputValue.toLowerCase())
                  }
                  leftColumns={leftColumns}
                  rightColumns={rightColumns}
                  showSearch={true}
                />
              </div>
            }
            {studentsList.length !== 0 && <div style={{
              marginTop: isMobile ? "20px" : "40px",
              width: "100%",
              display: "flex",
              flexDirection: "row",
              alignItems: isMobile ? "center" : "flex-end",
              justifyContent: isMobile ? "center" : "flex-end",
              padding: isMobile ? "0 10px" : "0"
            }}>
              <Button
                disabled={isInviting}
                className={s.newButton}
                onClick={handleInviteStudents}
                loading={isInviting}
              >
                Suivant
              </Button>
            </div>}
          </div>}
        </div>
      </div>
    </Drawer >
  );
};

export default ParentOrganismInviteStudents;